import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from './api';
import UserTable from './UserTable';
import CreateUserForm from './CreateUserForm';
import EditUserForm from './EditUserForm';
import UserGateManager from './UserGateManager';
import GateAccessManager from './GateAccessManager'; // Import GateAccessManager
import ActivityLog from './ActivityLog';
import AccessRequestsDashboard from './AccessRequestsDashboard';
import ManagedGatesDashboard from './ManagedGatesDashboard';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

function AdminDashboard({ claims }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [managingAccessForUser, setManagingAccessForUser] = useState(null);
  const [managingAccessForGate, setManagingAccessForGate] = useState(null); // New state for gates
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [view, setView] = useState('gates'); // 'gates', 'users', 'requests'
  
  const ownerId = claims?.ownerId;

  const fetchManagedUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callApi('listUsersForOwnerV2');
      setUsers(result.users);
    } catch (err) {
      setError('Kunne ikke hente brukere. ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'users') {
      fetchManagedUsers();
    }
  }, [view, fetchManagedUsers]);

  const handleFormFinished = () => {
    setEditingUser(null);
    setManagingAccessForUser(null);
    setManagingAccessForGate(null); // Reset gate manager as well
    setShowCreateForm(false);
    if (view === 'users') {
        fetchManagedUsers();
    }
  }

  const renderCurrentView = () => {
    // Forms take precedence over the main view
    if (editingUser) {
        return <EditUserForm user={editingUser} onFinished={handleFormFinished} />;
    }
    if (managingAccessForUser) {
        return <UserGateManager user={managingAccessForUser} ownerId={ownerId} onFinished={handleFormFinished} />;
    }
    if (managingAccessForGate) { // New condition for gate manager
        return <GateAccessManager gate={managingAccessForGate} ownerId={ownerId} onFinished={handleFormFinished} />;
    }
    if (showCreateForm) {
        return <CreateUserForm onUserCreated={handleFormFinished} onCancel={() => setShowCreateForm(false)} />;
    }

    // Main tabbed views
    switch (view) {
      case 'gates':
        return <ManagedGatesDashboard user={claims} onManageAccess={setManagingAccessForGate} />; // Corrected prop
      case 'users':
        return (
          <div>
            <h3>Dine Brukere</h3>
            <p>Dette er en oversikt over alle brukere som tilhører din organisasjon.</p>
            <button onClick={() => setShowCreateForm(true)} style={{ margin: '20px 0' }}>
                + Opprett Ny Bruker
            </button>
            {loading && <p>Laster brukere...</p>}
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {!loading && !error && (
                <UserTable
                    users={users}
                    onEditUser={setEditingUser}
                    onManageAccess={setManagingAccessForUser}
                    isLevel1List={true}
                />
            )}
          </div>
        );
      case 'requests':
        return <AccessRequestsDashboard ownerId={ownerId} onClose={() => setView('gates')} />;
      default:
        return null;
    }
  }
  
  const activeStyle = { backgroundColor: '#ccc', color: '#000', borderColor: '#000' };
  const navContainerStyle = {
    marginBottom: '20px',
    display: 'flex',
    gap: '10px',
    flexDirection: isMobile ? 'column' : 'row'
  };

  return (
    <div>
      <h2>Admin Dashboard</h2>
      <p>Her kan du administrere dine tildelte porter og brukere.</p>
      
      <div style={navContainerStyle}>
        <button onClick={() => setView('gates')} style={view === 'gates' ? { ...activeStyle, ...(isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {}) } : (isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {})}>Mine Porter</button>
        <button onClick={() => setView('users')} style={view === 'users' ? { ...activeStyle, ...(isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {}) } : (isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {})}>Brukere</button>
        <button onClick={() => setView('requests')} style={view === 'requests' ? { ...activeStyle, ...(isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {}) } : (isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {})}>Tilgangsforespørsler</button>
        <button onClick={() => setActivityLogOpen(true)} style={isMobile ? { width: '100%', boxSizing: 'border-box', marginLeft: 0 } : {}}>Aktivitetslogg</button>
      </div>

      {renderCurrentView()}

      {/* ActivityLog Modal */}
      <Dialog open={activityLogOpen} onClose={() => setActivityLogOpen(false)} fullWidth maxWidth="lg" PaperProps={{ style: { backgroundColor: 'var(--background-light)', color: 'var(--text-light)', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' } }}>
        <DialogTitle>Aktivitetslogg</DialogTitle>
        <DialogContent>
            <ActivityLog />
        </DialogContent>
        <DialogActions>
            <button onClick={() => setActivityLogOpen(false)}>Lukk</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AdminDashboard;